import type { ReactNode } from 'react';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Card, Link, Typography, CardContent } from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { useUnitStatSummaryByPolicyId, useUnitStatLossesSummaryByPolicyId } from 'src/api/hooks/usePolicyDetailsQueries';

import { EmptyContent } from 'src/components/empty-content';

import { formatDate, formatDollarAmount } from 'src/sections/employer-data/shared/utils/policyUtils';

import UnitStatAccordion from './unit-stat-accordion';
import UnitStatLossesTableSection from './unit-stat-losses-table-section';

type UnitStatSummarySectionProps = {
  comboId: string | undefined;
  coverageId: string | undefined;
  policyId: number | undefined;
};

const parseNumeric = (val: unknown): number | null => {
  if (val === null || val === undefined || val === '') return null;
  if (typeof val === 'number') return val;
  if (typeof val !== 'string') return null;

  const cleaned = val.replaceAll('$', '').replaceAll(',', '');
  const n = Number.parseFloat(cleaned);
  return Number.isNaN(n) ? null : n;
};

const formatRate = (value: unknown): string => {
  const numericValue = parseNumeric(value);
  return numericValue === null ? '' : numericValue.toFixed(4);
};

export default function UnitStatSummarySection({ comboId, coverageId, policyId }: Readonly<UnitStatSummarySectionProps>) {
  const {
    data: unitStatSummaryData,
    isError: isUnitStatSummaryError,
    isLoading: isUnitStatSummaryLoading,
    isFetching: isUnitStatSummaryFetching,
  } = useUnitStatSummaryByPolicyId(
    Number(comboId),
    Number(coverageId),
    Number(policyId)
  );

  const { data: lossesSummaryData } = useUnitStatLossesSummaryByPolicyId(
    Number(comboId),
    Number(coverageId),
    Number(policyId)
  );

  const unitStatSummaries = unitStatSummaryData?.data || [];
  const lossesSummary = lossesSummaryData?.data || [];

  const accordionItems = unitStatSummaries.map((summaryItem, index) => {
    const raw = summaryItem.policyPremiumDetails;

    return {
      id: summaryItem.id ?? `unit-stat-summary-${index}`,
      summaryDate: summaryItem.summaryDate ? formatDate(new Date(summaryItem.summaryDate)) : '',
      splits: (summaryItem.splits ?? []).map((split) => ({
        lastUnitUpdated: split.lastUnitUpdated ?? '',
        classCode: split.classCode ?? '',
        classDescription: split.classDescription ?? '',
        estimatedAnnualPayroll: formatDollarAmount(parseNumeric(split.estimatedAnnualPayroll)),
        ratePer100: formatRate(split.ratePer100),
        estimatedAnnualPremium: formatDollarAmount(parseNumeric(split.estimatedAnnualPremium)),
      })),
      policyPremiumDetails: {
        totalSubjectPremium: formatDollarAmount(parseNumeric(raw?.totalSubjectPremium)),
        totalStandardPremium: formatDollarAmount(parseNumeric(raw?.totalStandardPremium)),
        totalModifiedPremium: formatDollarAmount(parseNumeric(raw?.totalModifiedPremium)),
      },
    };
  });

  const totalSubjectPremiumForFinalSplit =
    accordionItems.length > 0
      ? accordionItems[accordionItems.length - 1].policyPremiumDetails.totalSubjectPremium
      : '';

  const hasUnitStatQueryParams = Boolean(comboId && coverageId && policyId);
  const isUnitStatSummaryInitialLoad =
    hasUnitStatQueryParams && (isUnitStatSummaryLoading || (isUnitStatSummaryFetching && !unitStatSummaryData));

  let summaryContent: ReactNode;
  if (isUnitStatSummaryError) {
    summaryContent = (
      <EmptyContent
        filled
        title="Unable to load unit stat summary"
        description="Try refreshing the page. If the issue persists, contact support."
        sx={{ py: 10 }}
      />
    );
  } else if (isUnitStatSummaryInitialLoad) {
    summaryContent = <Box sx={{ py: 10 }} />;
  } else if (accordionItems.length === 0) {
    summaryContent = (
      <EmptyContent
        filled
        title="No data to display"
        description="This section will be updated if the policy has any Unit Stat Summary exposures to report."
        sx={{ py: 10 }}
      />
    );
  } else {
    summaryContent = (
      <>
        <Box sx={{ p: 2, borderBottom: '0.0625em solid var(--palette-divider)' }}>
          <Typography variant="h6" sx={{ color: 'text.primary' }} data-testid="unit-stat-exposures-title">
            Exposures
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '3.5rem',
            px: 2,
            backgroundColor: 'background.neutral',
          }}
        >
          <Typography sx={{ color: 'text.secondary', fontWeight: 600 }} data-testid="unit-stat-exposures-date-header">
            Date
          </Typography>
        </Box>
        <Box sx={{ pt: '1rem', pb: '1rem' }}>
          <UnitStatAccordion items={accordionItems} />
        </Box>
      </>
    );
  }

  return (
    <Card
      sx={{
        borderRadius: 2,
        boxShadow: 'var(--customShadows-card)',
      }}
    >
      <CardContent sx={{ padding: 0 }}>
        <Box sx={{ padding: '1.5em 1em' }}>
          <Typography variant="h6" sx={{ color: 'text.primary', mb: 2 }}>
            Unit stat summary
          </Typography>
          <Box
            sx={{
              border: '0.0625em solid var(--palette-divider)',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            {summaryContent}
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 6,
              px: 2,
              pt: 1,
              pb: 0.5,
            }}
          >
            <Typography sx={{ fontWeight: 600, color: 'text.secondary' }}>
              Total Subject Premium
            </Typography>
            <Typography sx={{ fontWeight: 600, color: 'text.primary' }} data-testid="unit-stat-total-subject-premium-value">
              {totalSubjectPremiumForFinalSplit}
            </Typography>
          </Box>
          <UnitStatLossesTableSection lossesSummary={lossesSummary} />
        </Box>
        <Box sx={{ margin: '1rem', marginBottom: '0' }}>
          <Link
            component={RouterLink as React.ElementType}
            to={`/dashboard/employer-data/combo/${comboId}/coverage/${coverageId}/policy/${policyId}/policy-details`}
            state={{ tab: 'unitStatReports', innerTab: 'Losses' }}
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
            data-testid="see-all-unit-stat-reports-button"
          >
            See all unit stat reports
            <ArrowForwardIcon sx={{ marginLeft: '0.5rem' }} />
          </Link>
        </Box>
      </CardContent>
    </Card>
  );
}
